import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Logo from '../components/common/Logo';
import { MARITAL_STATUS_OPTIONS, EDUCATION_LEVELS, EMPLOYMENT_STATUS_OPTIONS, LOAN_PURPOSES } from '../utils/constants';
import { saveProfileStep2, getUserProfile } from '../services/firestore';
import useAuthStore from '../stores/authStore';

const ProfileStep2 = () => {
  // Calculate default date (18 years ago)
  const getDefaultDate = () => {
    const today = new Date();
    const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    return eighteenYearsAgo.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    dateOfBirth: '',
    maritalStatus: '',
    education: '',
    employmentStatus: '',
    monthlyIncome: '',
    loanPurpose: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, isAuthenticated, setProfile } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    // Load existing profile data if available
    const loadProfile = async () => {
      if (user?.uid) {
        try {
          const result = await getUserProfile(user.uid);
          if (result.success && result.profile) {
            setFormData(prev => ({
              ...prev,
              dateOfBirth: result.profile.dateOfBirth || getDefaultDate(),
              maritalStatus: result.profile.maritalStatus || '',
              education: result.profile.education || '',
              employmentStatus: result.profile.employmentStatus || '',
              monthlyIncome: result.profile.monthlyIncome || '',
              loanPurpose: result.profile.loanPurpose || ''
            }));
          } else {
            // If no existing profile data, set default date
            setFormData(prev => ({
              ...prev,
              dateOfBirth: getDefaultDate()
            }));
          }
        } catch (error) {
          console.error('Load profile error:', error);
          // Set default date on error as well
          setFormData(prev => ({
            ...prev,
            dateOfBirth: getDefaultDate()
          }));
        }
      }
    };

    loadProfile();
  }, [user, isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital status is required';
    }

    if (!formData.education) {
      newErrors.education = 'Education level is required';
    }

    if (!formData.employmentStatus) {
      newErrors.employmentStatus = 'Employment status is required';
    }

    if (!formData.monthlyIncome.trim()) {
      newErrors.monthlyIncome = 'Monthly income is required';
    } else if (isNaN(formData.monthlyIncome) || Number(formData.monthlyIncome) < 0) {
      newErrors.monthlyIncome = 'Please enter a valid income amount';
    }

    if (!formData.loanPurpose) {
      newErrors.loanPurpose = 'Loan purpose is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    try {
      const profileData = {
        ...formData,
        monthlyIncome: Number(formData.monthlyIncome)
      };

      const result = await saveProfileStep2(user.uid, profileData);
      
      if (result.success) {
        // Update profile in store
        setProfile({ ...profileData, profileCompleted: true });
        navigate('/loan-limits');
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Save error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/profile-step-1');
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="text-center mb-6">
          <Logo size="lg" className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Additional Information
          </h1>
          <p className="text-gray-600 mb-4">
            Step 2 of 2 - Complete Your Profile
          </p>
          
          {/* Step indicator */}
          <div className="step-indicator">
            <div className="step-dot step-dot-active"></div>
            <div className="step-dot step-dot-active"></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Date of Birth"
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            error={errors.dateOfBirth}
            max={new Date().toISOString().split('T')[0]} // Prevent future dates
            required
          />

          <Select
            label="Marital Status"
            name="maritalStatus"
            options={MARITAL_STATUS_OPTIONS}
            value={formData.maritalStatus}
            onChange={handleInputChange}
            error={errors.maritalStatus}
            placeholder="Select marital status"
            required
          />

          <Select
            label="Level of Education"
            name="education"
            options={EDUCATION_LEVELS}
            value={formData.education}
            onChange={handleInputChange}
            error={errors.education}
            placeholder="Select education level"
            required
          />

          <Select
            label="Employment Status"
            name="employmentStatus"
            options={EMPLOYMENT_STATUS_OPTIONS}
            value={formData.employmentStatus}
            onChange={handleInputChange}
            error={errors.employmentStatus}
            placeholder="Select employment status"
            required
          />

          <Input
            label="Monthly Income (KES)"
            type="number"
            name="monthlyIncome"
            placeholder="Enter your monthly income"
            value={formData.monthlyIncome}
            onChange={handleInputChange}
            error={errors.monthlyIncome}
            min="0"
            required
          />

          <Select
            label="Loan Purpose"
            name="loanPurpose"
            options={LOAN_PURPOSES}
            value={formData.loanPurpose}
            onChange={handleInputChange}
            error={errors.loanPurpose}
            placeholder="Select loan purpose"
            required
          />

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-red-700 text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileStep2;